import FocusOutDetector from './detectors/FocusOutDetector';
import InputDetector from './detectors/InputDetector';
import MouseMoveDetector from './detectors/MouseMoveDetector';
import PageHideDetector from './detectors/PageHideDetector';
import SubmitDetector from './detectors/SubmitDetector';
import VisibilityChangeDetector from './detectors/VisibilityChangeDetector';
import VisibleElementDetector from './detectors/VisibleElementDetector';
import Clarity from './services/Clarity';
import Configuration from './services/Configuration';
import DataBundler from './services/DataBundler';
import DetectionTimeline from './services/DetectionTimeline';
import PageHandler from './services/PageHandler';
import AnonymousUser from './services/AnonymousUser';
import UuidGenerator from './services/UuidGenerator';
import SessionManager from './services/sessions/SessionManager';
import {
  SESSION_TIMEOUT,
  EVENT_PAGE_HIDE,
  EVENT_VISIBILITY_CHANGE,
  UPFLOWY_DOM_ID,
  UPFLOWY_INTENT_SCRIPT_SELECTOR,
} from './settings';
import {
  Detector,
  OnDetectionCallback,
  PagePayload,
  Persistence,
} from './types';

class Watcher {
  foregroundDetectors: Detector<any>[];

  backgroundDetectors: Detector<any>[];

  detectionTimeline: DetectionTimeline;

  sessionManager: SessionManager;

  anonymousUser: AnonymousUser;

  dataBundler: DataBundler;

  idMap: WeakMap<Node, string> = new WeakMap();

  currentPage: PagePayload;

  pagesRegistry: Map<string, PagePayload> = new Map();

  pageHandler: PageHandler;

  config: Configuration;

  persistence: Persistence;

  constructor({ elements, config, storage }:
  { elements: Element[]; config: Configuration; storage: Persistence }) {
    this.config = config;
    this.persistence = storage;
    this.pageHandler = new PageHandler(config.getWorkspaceId());
    this.dataBundler = new DataBundler();
    this.detectionTimeline = new DetectionTimeline();

    Watcher.setBaseTag();
    this.setUniqueDataUWAttribute();

    this.anonymousUser = new AnonymousUser({
      persistence: this.persistence,
      workspaceId: config.getWorkspaceId(),
    });

    this.sessionManager = new SessionManager({
      sessionTimeout: SESSION_TIMEOUT,
      persistence: this.persistence,
      workspaceId: config.getWorkspaceId(),
      onSessionStart: async (session) => {
        try {
          const workspaceConfig = await this.config.getWorkspaceConfig();
          if (workspaceConfig.clarityEnabled) {
            try {
              const clarity = new Clarity();
              const clarityUserId = await clarity.getClarityId();
              session.addMetadata({ clarityUserId });
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn("Couldn't retrieve Clarity ID", e);
            }
            try {
              window.clarity('identify', 'upflowy-intent', session.id, this.currentPage.id);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn("Couldn't integrate with ClarityJS", e);
            }
          }
        } finally {
          await this.anonymousUser.syncSession(session.serialize());
        }
      },
    });

    const pagePayload = Watcher.constructPagePayload(
      window.location.href,
      this.config.getWorkspaceId(),
      this.anonymousUser.getAnonymousUserId(),
      this.sessionManager.getCurrentSession().id,
    );
    this.currentPage = pagePayload;

    const onDetection: OnDetectionCallback<Record<string, unknown>> = (
      target: Element | Document,
      evt: Event,
      startTime: number,
      duration: number | null = null,
      properties: Record<string, unknown> = {},
    ) => {
      const eventTarget = target === document ? document.documentElement : (target as Element);
      const detection = this.detectionTimeline.addDetection(
        eventTarget,
        evt,
        this.sessionManager.getCurrentSession(),
        startTime,
        duration,
        properties,
      );

      const serializedDetection = DetectionTimeline.serializeDetection(detection);
      this.dataBundler.handlePayload({
        ...serializedDetection,
        workspaceId: this.config.getWorkspaceId(),
        anonymousUserId: this.anonymousUser.getAnonymousUserId(),
        pageId: this.currentPage.id,
      });

      this.sessionManager.senseForContactDetails(evt, () => {
        this.dataBundler.upload();
        this.anonymousUser.syncSession(this.sessionManager.getCurrentSession().serialize());
      });

      this.handlePageState(evt);
    };

    this.foregroundDetectors = [
      new VisibleElementDetector<Record<string, unknown>>(elements, onDetection),
      new MouseMoveDetector(onDetection),
      new InputDetector(onDetection),
      new SubmitDetector(onDetection),
      new FocusOutDetector(onDetection),
    ];

    this.backgroundDetectors = [
      new VisibilityChangeDetector(onDetection), new PageHideDetector(onDetection),
    ];

    this.backgroundDetectors.forEach((detector) => detector.start());

    this.start();
  }

  // eslint-disable-next-line max-len
  static constructPagePayload(href: string, workspaceId: string, anonymousUserId: string, sessionId: string) {
    const documentElementClone = document.documentElement.cloneNode(true) as HTMLElement;
    documentElementClone.querySelector(UPFLOWY_INTENT_SCRIPT_SELECTOR)?.remove();
    const content = documentElementClone.outerHTML;

    const pagePayload: PagePayload = {
      id: UuidGenerator.generate(),
      sessionId,
      anonymousUserId,
      workspaceId,
      href,
      lang: document.documentElement.lang,
      title: document.title,
      content,
      contentByteSize: new Blob([content]).size,
    };
    return pagePayload;
  }

  async sendPage(workspaceId: string, anonymousUserId: string, sessionId: string) {
    const { href } = window.location;
    const page = this.pagesRegistry.get(href);
    if (!page) {
      // eslint-disable-next-line max-len
      const pagePayload = Watcher.constructPagePayload(href, workspaceId, anonymousUserId, sessionId);
      this.currentPage = pagePayload;
      await this.pageHandler.upload(pagePayload);
      this.pagesRegistry.set(href, pagePayload);
    }
    return this;
  }

  async start() {
    await this.sendPage(
      this.config.getWorkspaceId(),
      this.anonymousUser.getAnonymousUserId(),
      this.sessionManager.getCurrentSession().id,
    );
    this.foregroundDetectors.forEach((detector) => detector.start());
    return this;
  }

  stop() {
    this.foregroundDetectors.forEach((detector) => detector.stop());
    this.dataBundler.upload();
    return this;
  }

  static setBaseTag() {
    const existingBaseTag = document.querySelector('base');
    if (!existingBaseTag) {
      // Create a new <base> tag
      const baseTag = document.createElement('base');
      baseTag.href = window.location.href;
      // Get the <head> element and append the <base> tag to it
      const head = document.head || document.getElementsByTagName('head')[0];
      head.insertBefore(baseTag, head.firstChild);
    }
  }

  setUniqueDataUWAttribute() {
    let index = 0;
    const elements = document.querySelectorAll('*');
    elements.forEach((element) => {
      // eslint-disable-next-line no-plusplus
      const id = `${element.id || index++}`;
      this.idMap.set(element, id);
      element.setAttribute(UPFLOWY_DOM_ID, id);
    });
  }

  handlePageState(evt: Event) {
    if (evt.type === EVENT_PAGE_HIDE
      || (evt.type === EVENT_VISIBILITY_CHANGE && document.visibilityState === 'hidden')
    ) {
      return this.stop();
    }
    if (evt.type === EVENT_VISIBILITY_CHANGE && document.visibilityState === 'visible') {
      return this.start();
    }
    return this;
  }
}

export default Watcher;
